import React, { useState, useCallback, useEffect, useRef } from 'react';
import { X, Edit2, Check, Copy, Wind, Flame } from 'lucide-react';
import { DeviceTemplateProps, TraitStyleConfig, AttributeStyleConfig, IconStyleId } from '../../types';

// DeviceIcon import
import DeviceIcon from '../../../deviceIcons';

// Import Dynamic Trait Renderer
import { DynamicTrait, DynamicAttribute, getTraitStyleId, getAttributeStyleId } from '../../DynamicTraitRenderer';

/**
 * ERV (Energy Recovery Ventilator) Template 01 - Dashboard
 * 全熱交換器模板
 * 
 * 設備能力：
 * - OnOff: 電源開關
 * - CustomModes (FanSpeed): 風速 2-6
 * - CustomModes (Mode): 運轉模式 0, 1, 2
 * - CustomModes (Timer): 定時 0-24 小時
 * - SensorState: PM2.5, CO2 感測器
 * - TemperatureSetting (queryOnly): 室內溫度
 * - HumiditySetting (queryOnly): 室內濕度
 */
export const Template01_Dashboard: React.FC<DeviceTemplateProps> = ({
  device,
  isExecutingCommand,
  onTraitControl,
  onClose,
  onNicknameSave,
  config,
  isMerged,
  endpointCount,
  onToggleMerge
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState(device.nickname || device.deviceName);
  const [chartSensorType, setChartSensorType] = useState<'temperature' | 'humidity' | 'pm25' | 'co2'>('temperature');
  const blockSyncUntilRef = useRef<number>(0);
  const isSavingRef = useRef(false);

  // Extract styles from config
  const traitStyles: TraitStyleConfig = config?.traitStyles || {};
  const attributeStyles: AttributeStyleConfig = config?.attributeStyles || {};
  const smallIconStyle: IconStyleId = (config?.smallIconStyle || config?.iconStyle || 'Style01') as IconStyleId;
  const bigIconStyle: IconStyleId = (config?.bigIconStyle || config?.iconStyle || 'Style01') as IconStyleId;
  const traitOrder: string[] = config?.traitOrder || ['AQIDisplay', 'SensorDisplay', 'SensorChart', 'OnOff', 'FanSpeed', 'ThermostatMode', 'Mode', 'Timer'];
  const hiddenComponents: string[] = config?.hiddenComponents || [];

  // Sync tempName when device prop changes, but skip if we just saved
  useEffect(() => {
    if (!isEditingName && Date.now() > blockSyncUntilRef.current) {
      setTempName(device.nickname || device.deviceName);
    }
  }, [device.nickname, device.deviceName, isEditingName]);

  const handleBlur = () => {
    if (!isSavingRef.current) {
      setIsEditingName(false);
    }
  };
  
  const handleSaveName = async () => {
    if (onNicknameSave && tempName.trim()) {
      isSavingRef.current = true;
      const savedName = tempName.trim();
      blockSyncUntilRef.current = Date.now() + 3000;
      await onNicknameSave(device.deviceId, savedName);
      setIsEditingName(false);
      isSavingRef.current = false;
    }
  };

  const attributes = device.attributes || {};
  // ✅ 提取 values 物件（用於解析 states[0].values 結構的資料）
  const valuesObj = (attributes as any)?.values || {};
  // customControls 包含 Modes 的中英文名稱 (從 QueryDevices 獲取)
  const customControls = (device as any).customControls || {};
  // 嘗試從 HEATER endpoint 獲取，或者直接從 root 獲取
  const modesControls = customControls?.HEATER?.Modes || customControls?.Modes || {};
  
  // 輔助函數：從 customControls 獲取翻譯標籤
  const getSettingLabel = (controlName: string, settingValue: string, fallback: string): string => {
    const control = modesControls[controlName];
    if (!control) return fallback;
    
    // 優先使用繁體中文
    const zhTW = control['zh-TW'] || control['zh_TW'];
    if (zhTW?.SettingSynonym?.[settingValue]?.[0]) {
      return zhTW.SettingSynonym[settingValue][0];
    }
    
    // Fallback 到英文
    const en = control['en'];
    if (en?.SettingSynonym?.[settingValue]?.[0]) {
      return en.SettingSynonym[settingValue][0];
    }
    
    return fallback;
  };
  
  // Extract fan speeds from CustomModes with labels from customControls
  const { fanSpeeds, fanSpeedLabels } = (() => {
    const customModes = attributes.availableCustomModes || [];
    const fanSpeedMode = customModes.find((m: any) => m.name === 'FanSpeed');
    const speeds: string[] = fanSpeedMode?.settings || ['2', '3', '4', '5', '6'];
    
    const labels: Record<string, string> = {};
    speeds.forEach((speed: string) => {
      // 使用 customControls 的翻譯，如果沒有則使用數字
      labels[speed] = getSettingLabel('FanSpeed', speed, speed);
    });
    
    return { fanSpeeds: speeds, fanSpeedLabels: labels };
  })();
  
  // Extract modes from CustomModes with labels from customControls
  const { modes, modeLabels } = (() => {
    const customModes = attributes.availableCustomModes || [];
    const modeConfig = customModes.find((m: any) => m.name === 'Mode');
    const modeSettings: string[] = modeConfig?.settings || ['0', '1', '2'];
    
    // 使用 customControls 的翻譯作為標籤
    const defaultLabels = ['自動', '全熱', '普通', '節能', '強力'];
    const labels: Record<string, string> = {};
    modeSettings.forEach((mode, index) => {
      labels[mode] = getSettingLabel('Mode', mode, defaultLabels[index] || `模式 ${mode}`);
    });
    
    return { modes: modeSettings, modeLabels: labels };
  })();
  
  // Extract timer options
  const timerOptions = (() => {
    const customModes = attributes.availableCustomModes || [];
    const timerConfig = customModes.find((m: any) => m.name === 'Timer');
    return timerConfig?.settings || [];
  })();
  
  // Extract thermostat modes (fan-only, heat) - separate from CustomModes.Mode
  const { thermostatModes, thermostatModeLabels } = (() => {
    const availableModes = attributes.availableThermostatModes || 
                           (device as any).availableThermostatModes || 
                           'fan-only,heat';
    const modesList = typeof availableModes === 'string' 
      ? availableModes.split(',').map((m: string) => m.trim())
      : availableModes;
    
    const labels: Record<string, string> = {
      'fan-only': '送風',
      'heat': '暖氣',
      'cool': '冷氣',
      'auto': '自動',
      'off': '關閉'
    };
    
    return { thermostatModes: modesList, thermostatModeLabels: labels };
  })();
  
  // Get current state values
  // Check both device root level and attributes for compatibility
  // Handle multiple formats: object, { value: object }, or nested under 'values'
  // ✅ 修正：也檢查 attributes.values.currentModeSettings.value 路徑
  const rawModeSettings = (device as any).currentModeSettings || 
                          valuesObj?.currentModeSettings?.value ||
                          (attributes as any)?.currentModeSettings?.value ||
                          (attributes as any)?.currentModeSettings ||
                          {};
  // Ensure currentModeSettings is always an object
  const currentModeSettings = (rawModeSettings && typeof rawModeSettings === 'object' && !Array.isArray(rawModeSettings))
    ? (rawModeSettings.value && typeof rawModeSettings.value === 'object' ? rawModeSettings.value : rawModeSettings)
    : {};
  const currentFanSpeed = String(currentModeSettings.FanSpeed || device.fanSpeed || '3');
  const currentMode = String(currentModeSettings.Mode || '0');
  const currentTimer = Number(currentModeSettings.Timer || 0);
  
  // Get current thermostat mode (fan-only, heat)
  // ✅ 修正：也檢查 attributes.values.thermostatMode.value 路徑
  const currentThermostatMode = (device as any).thermostatMode || 
                                 (device as any).mode ||
                                 valuesObj?.thermostatMode?.value ||
                                 (attributes as any)?.thermostatMode?.value || 
                                 'fan-only';
  
  // Get sensor data
  // Check both device root level and attributes for compatibility
  // Handle multiple formats: array, { value: array }, or nested under 'values'
  // ✅ 修正：也檢查 attributes.values.currentSensorStateData.value 路徑
  const rawSensorData = (device as any).currentSensorStateData || 
                        valuesObj?.currentSensorStateData?.value ||
                        (attributes as any)?.currentSensorStateData?.value ||
                        (attributes as any)?.currentSensorStateData ||
                        [];
  // Ensure sensorData is always an array
  const sensorData = Array.isArray(rawSensorData) 
    ? rawSensorData 
    : (rawSensorData?.value && Array.isArray(rawSensorData.value)) 
      ? rawSensorData.value 
      : [];
  
  // PM2.5: Check currentSensorStateData first, then fallback to direct device properties
  const pm25FromSensorData = sensorData.find((s: any) => s.name === 'PM2.5')?.rawValue;
  const pm25FromDevice = (device as any).pm25 ?? (device as any).pm2_5;
  const pm25ValueRaw = Array.isArray(pm25FromDevice) ? pm25FromDevice[0] : pm25FromDevice;
  const pm25Value = pm25FromSensorData ?? pm25ValueRaw ?? 0;
  
  // CO2: Check currentSensorStateData first, then fallback to direct device properties
  const co2FromSensorData = sensorData.find((s: any) => s.name === 'CarbonDioxideLevel')?.rawValue;
  const co2FromDevice = (device as any).co2;
  const co2ValueRaw = Array.isArray(co2FromDevice) ? co2FromDevice[0] : co2FromDevice;
  const co2Value = co2FromSensorData ?? co2ValueRaw ?? 0;
  
  // Debug log for sensor values
  console.log('🌀 [ERV Template] Sensor values:', {
    pm25FromSensorData, pm25FromDevice, pm25Value,
    co2FromSensorData, co2FromDevice, co2ValueRaw, co2Value,
    sensorData,
    deviceCo2: (device as any).co2,
    devicePm25: (device as any).pm25,
  });
  
  // Temperature and humidity (queryOnly)
  // ✅ 修正：也檢查 attributes.values 路徑
  const temperature = device.temperature ?? 
                      (device as any).thermostatTemperatureAmbient ??
                      valuesObj?.thermostatTemperatureAmbient?.value;
  const humidity = (device as any).humidity ?? 
                   (device as any).humidityAmbientPercent ??
                   valuesObj?.humidityAmbientPercent?.value;
  
  // Generate mock chart data for preview (includes all sensor types)
  const generateMockChartData = () => {
    const data = [];
    const now = new Date();
    for (let i = 24; i >= 0; i--) {
      const time = new Date(now.getTime() - i * 60 * 60 * 1000);
      data.push({
        time: time.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' }),
        temperature: 22 + Math.sin(i * 0.3) * 3 + Math.random() * 0.5,
        humidity: 55 + Math.cos(i * 0.25) * 8 + Math.random() * 2,
        pm25: Math.max(0, 15 + Math.sin(i * 0.2) * 10 + Math.random() * 5),
        co2: Math.max(400, 600 + Math.sin(i * 0.15) * 200 + Math.random() * 50),
      });
    }
    return data;
  };
  
  // Device state
  // ✅ 修正：也檢查 attributes.values.on.value 路徑
  const isDeviceOn = device.isOn ?? valuesObj?.on?.value ?? false;
  const isDeviceOff = !isDeviceOn;

  // Handlers
  const handlePowerToggle = () => {
    onTraitControl(device, 'OnOff', isDeviceOn ? 'off' : 'on');
  };

  const handleFanSpeedChange = (speed: string) => {
    onTraitControl(device, 'CustomModes', 'set', { 
      updateModeSettings: { FanSpeed: speed }
    });
  };

  const handleModeChange = (mode: string) => {
    onTraitControl(device, 'CustomModes', 'set', { 
      updateModeSettings: { Mode: mode }
    });
  };

  const handleTimerChange = (timer: string) => {
    onTraitControl(device, 'CustomModes', 'set', { 
      updateModeSettings: { Timer: timer }
    });
  };
  
  const handleThermostatModeChange = (mode: string) => {
    onTraitControl(device, 'ThermostatMode', 'set', { 
      thermostatMode: mode 
    });
  };

  // Handle backdrop click - close modal or cancel editing
  const handleBackdropClick = useCallback(() => {
    if (isEditingName) {
      setIsEditingName(false);
      setTempName(device.nickname || device.deviceName || '');
    } else {
      onClose();
    }
  }, [isEditingName, device.nickname, device.deviceName, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4" onClick={handleBackdropClick}>
      <div className="bg-gradient-to-b from-[#0a1628] via-[#0d1d35] to-[#0a1628] rounded-3xl w-full max-w-sm max-h-[90vh] overflow-hidden shadow-2xl border border-slate-800/50" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <DeviceIcon 
              deviceType="erv" 
              isOn={isDeviceOn} 
              size={20}
              styleId={smallIconStyle}
              className="flex-shrink-0"
            />
            {isEditingName ? (
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <input
                  type="text"
                  value={tempName}
                  onChange={(e) => setTempName(e.target.value)}
                  className="flex-1 min-w-0 px-2 py-1 text-sm font-bold border border-slate-600 rounded bg-slate-800/80 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveName();
                    }
                    if (e.key === 'Escape') setIsEditingName(false);
                  }}
                  onBlur={handleBlur}
                />
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleSaveName}
                  className="p-1 text-green-500 hover:text-green-400 flex-shrink-0"
                  title="儲存"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setIsEditingName(false)}
                  className="p-1 text-slate-400 hover:text-slate-300 flex-shrink-0"
                  title="取消"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-base font-bold text-white truncate">{device.nickname || device.deviceName}</h2>
                {onNicknameSave && (
                  <button 
                    onClick={() => {
                      setTempName(device.nickname || device.deviceName);
                      setIsEditingName(true);
                    }}
                    className="p-1 hover:bg-slate-700/50 rounded transition-colors flex-shrink-0"
                  >
                    <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                  </button>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <DynamicAttribute
              attributeName="StatusIndicator"
              styleId={getAttributeStyleId('StatusIndicator', attributeStyles)}
              componentProps={{
                online: device.status === 'online',
                label: device.status === 'online' ? 'ONLINE' : 'OFFLINE'
              }}
            />
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-slate-700/50 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-5 pb-6 space-y-5 overflow-y-auto max-h-[calc(90vh-140px)]">
          
          {/* Big Icon - ERV Visual */}
          {!hiddenComponents.includes('BigIcon') && (
            <div className="flex justify-center py-4">
              <div className={`w-28 h-28 rounded-full flex items-center justify-center transition-all duration-300 ${
                isDeviceOn 
                  ? 'bg-gradient-to-br from-teal-500/30 to-cyan-500/30 border-2 border-teal-400/50 shadow-lg shadow-teal-500/30' 
                  : 'bg-slate-800/50 border-2 border-slate-700'
              }`}>
                <DeviceIcon
                  deviceType="erv"
                  isOn={isDeviceOn}
                  size={56}
                  styleId={bigIconStyle}
                  className={isDeviceOn ? 'text-teal-400' : 'text-slate-500'}
                />
              </div>
            </div>
          )}

          {/* Dynamically rendered components based on order */}
          {traitOrder.map((componentName) => {
            // Check if component is hidden
            if (hiddenComponents.includes(componentName)) {
              return null;
            }
            
            switch (componentName) {
              // AQI Display - PM2.5 and CO2
              case 'AQIDisplay':
                return (
                  <DynamicAttribute
                    key="AQIDisplay"
                    attributeName="AQIDisplay"
                    styleId={getAttributeStyleId('AQIDisplay', attributeStyles)}
                    componentProps={{
                      pm25: pm25Value,
                      co2: co2Value,
                      showLabels: true
                    }}
                  />
                );
              
              // Temperature & Humidity Display
              case 'SensorDisplay':
                if (temperature === undefined && humidity === undefined) return null;
                return (
                  <div key="SensorDisplay" className="grid grid-cols-2 gap-3">
                    {/* Temperature */}
                    {temperature !== undefined && (
                      <DynamicAttribute
                        attributeName="SensorDisplay"
                        styleId={getAttributeStyleId('SensorDisplay', attributeStyles)}
                        componentProps={{
                          type: 'temperature',
                          value: temperature,
                          unit: '°C',
                          label: '溫度',
                          size: 'medium'
                        }}
                      />
                    )}
                    
                    {/* Humidity */}
                    {humidity !== undefined && (
                      <DynamicAttribute
                        attributeName="SensorDisplay"
                        styleId={getAttributeStyleId('SensorDisplay', attributeStyles)}
                        componentProps={{
                          type: 'humidity',
                          value: humidity,
                          unit: '%',
                          label: '濕度',
                          size: 'medium'
                        }}
                      />
                    )}
                  </div>
                );
              
              case 'SensorChart':
                // Generate mock chart data for preview, or use real historical data
                const chartData = (device as any).chartData || generateMockChartData();
                const chartStyleId = getAttributeStyleId('SensorChart', attributeStyles);
                
                // Sensor type options for the selector
                const sensorOptions: { value: string; label: string; color: string; unit: string }[] = [
                  { value: 'temperature', label: '溫度', color: '#fb923c', unit: '°C' },
                  { value: 'humidity', label: '濕度', color: '#38bdf8', unit: '%' },
                  { value: 'pm25', label: 'PM2.5', color: '#4ade80', unit: 'µg/m³' },
                  { value: 'co2', label: 'CO₂', color: '#a78bfa', unit: 'ppm' },
                ];
                
                const selectedSensor = sensorOptions.find(s => s.value === chartSensorType) || sensorOptions[0];
                
                // Build chart props based on selected sensor type and style
                const buildChartProps = () => {
                  // For Style03 (DualAxis), show dual temperature/humidity
                  if (chartStyleId === 'Style03') {
                    return {
                      data: chartData,
                      showTemperature: true,
                      showHumidity: true,
                      height: 150,
                      title: '溫濕度趨勢'
                    };
                  }
                  
                  // For single-line charts (Style01, Style02)
                  return {
                    data: chartData.map((d: any) => ({ 
                      ...d, 
                      value: d[chartSensorType] ?? d.temperature 
                    })),
                    dataKey: 'value',
                    color: selectedSensor.color,
                    unit: selectedSensor.unit,
                    height: 150,
                    title: `${selectedSensor.label}趨勢`
                  };
                };
                
                return (
                  <div key="SensorChart" className="mt-2 space-y-2">
                    {/* Sensor type selector - always show to allow switching between modes */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {sensorOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => setChartSensorType(option.value as any)}
                          className={`px-2 py-1 text-xs rounded-md transition-all ${
                            chartSensorType === option.value
                              ? 'bg-teal-500/20 text-teal-400 border border-teal-500/50'
                              : 'bg-slate-700/50 text-slate-400 border border-slate-600 hover:bg-slate-600/50'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <DynamicAttribute
                      attributeName="SensorChart"
                      styleId={chartStyleId}
                      componentProps={buildChartProps()}
                    />
                  </div>
                );
              
              case 'OnOff':
                return (
                  <div key="OnOff" className="py-2 border-b border-slate-700/30">
                    <DynamicTrait
                      traitName="OnOff"
                      styleId={getTraitStyleId('OnOff', traitStyles)}
                      componentProps={{
                        isOn: isDeviceOn,
                        onToggle: handlePowerToggle,
                        disabled: isExecutingCommand,
                        loading: isExecutingCommand,
                        label: "電源"
                      }}
                    />
                  </div>
                );
              
              case 'FanSpeed':
                return (
                  <div key="FanSpeed" className={`transition-opacity ${isDeviceOff ? 'opacity-40 pointer-events-none' : ''}`}>
                    <DynamicTrait
                      traitName="FanSpeed"
                      styleId={getTraitStyleId('FanSpeed', traitStyles)}
                      componentProps={{
                        value: currentFanSpeed,
                        options: fanSpeeds,
                        disabled: isExecutingCommand || isDeviceOff,
                        loading: isExecutingCommand,
                        onChange: handleFanSpeedChange,
                        labels: fanSpeedLabels,
                        label: '風速'
                      }}
                    />
                  </div>
                );
              
              case 'Mode':
                return (
                  <div key="Mode" className={`transition-opacity ${isDeviceOff ? 'opacity-40 pointer-events-none' : ''}`}>
                    <DynamicTrait
                      traitName="Mode"
                      styleId={getTraitStyleId('Mode', traitStyles)}
                      componentProps={{
                        value: currentMode,
                        options: modes,
                        disabled: isExecutingCommand || isDeviceOff,
                        loading: isExecutingCommand,
                        onChange: handleModeChange,
                        labels: modeLabels,
                        label: '運轉模式'
                      }}
                    />
                  </div>
                );
              
              case 'ThermostatMode':
                if (thermostatModes.length === 0) return null;
                // Custom icons for thermostat modes
                const thermostatModeIcons: Record<string, React.ElementType> = {
                  'fan-only': Wind,
                  'heat': Flame,
                  'cool': Wind,  // Use wind for cool as well
                  'auto': Wind
                };
                return (
                  <div key="ThermostatMode" className={`transition-opacity ${isDeviceOff ? 'opacity-40 pointer-events-none' : ''}`}>
                    <DynamicTrait
                      traitName="Mode"
                      styleId={getTraitStyleId('Mode', traitStyles)}
                      componentProps={{
                        value: currentThermostatMode,
                        options: thermostatModes,
                        disabled: isExecutingCommand || isDeviceOff,
                        loading: isExecutingCommand,
                        onChange: handleThermostatModeChange,
                        labels: thermostatModeLabels,
                        icons: thermostatModeIcons,
                        label: '運轉類型'
                      }}
                    />
                  </div>
                );
              
              case 'Timer':
                if (timerOptions.length === 0) return null;
                // Create timer labels (0 = 關閉, 1-24 = N 小時)
                const timerLabels: Record<string, string> = {};
                timerOptions.forEach((t: string) => {
                  timerLabels[t] = t === '0' ? '關閉' : `${t}h`;
                });
                return (
                  <div key="Timer" className={`transition-opacity ${isDeviceOff ? 'opacity-40 pointer-events-none' : ''}`}>
                    <DynamicTrait
                      traitName="Timer"
                      styleId={getTraitStyleId('Timer', traitStyles)}
                      componentProps={{
                        value: String(currentTimer),
                        options: timerOptions,
                        disabled: isExecutingCommand || isDeviceOff,
                        loading: isExecutingCommand,
                        onChange: handleTimerChange,
                        labels: timerLabels,
                        label: '定時關機',
                        unit: '小時'
                      }}
                    />
                  </div>
                );
              
              default:
                return null;
            }
          })}

        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-800/50 bg-slate-900/30 space-y-2">
          {/* Multi-endpoint device merge toggle */}
          {device.isMultiChannel && endpointCount && endpointCount > 1 && onToggleMerge && (
            <div className="flex items-center justify-between py-1 px-2 bg-slate-700/30 rounded-lg">
              <span className="text-xs text-slate-400">
                多端點設備 ({endpointCount} 個)
              </span>
              <button
                onClick={() => onToggleMerge(device.baseSN)}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  isMerged 
                    ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' 
                    : 'bg-slate-600/50 text-slate-300 hover:bg-slate-600'
                }`}
              >
                {isMerged ? '顯示為個別方塊' : '顯示為單一方塊'}
              </button>
            </div>
          )}
          
          {/* Location & SN with copy */}
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{device.location || device.groupName || ''}</span>
            <div className="flex items-center gap-1">
              <span className="font-mono">{device.deviceId || device.baseSN}</span>
              <button
                onClick={() => navigator.clipboard.writeText(device.deviceId || device.baseSN)}
                className="text-slate-400 hover:text-white transition-colors"
                title="複製設備 SN"
              >
                <Copy className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Template01_Dashboard;
